import { useMemo, useState } from 'react';
import {
    AlertCircle,
    AlertTriangle,
    CalendarDays,
    CheckCircle2,
    Circle,
    Columns3,
    GripVertical,
    List,
    Plus,
    Search,
    X
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import type { Ticket, TicketPriority, TicketStatus } from '../../types';

type BoardStatus = Exclude<TicketStatus, 'deleted'>;
type BoardScope = 'my' | 'all';

const BOARD_COLUMNS: Array<{
    id: BoardStatus;
    label: string;
    color: string;
    icon: typeof Circle;
}> = [
    { id: 'open', label: 'Open', color: '#3b82f6', icon: Circle },
    { id: 'in_progress', label: 'In Progress', color: '#f59e0b', icon: AlertCircle },
    { id: 'resolved', label: 'Resolved', color: '#10b981', icon: CheckCircle2 },
    { id: 'closed', label: 'Closed', color: '#64748b', icon: CheckCircle2 }
];

const priorityRank: Record<TicketPriority, number> = {
    urgent: 0,
    high: 1,
    medium: 2,
    low: 3
};

export default function TicketBoard() {
    const {
        tickets,
        projects,
        users,
        departments,
        currentUser,
        updateTicket,
        setActiveView,
        setShowCreateTicket,
        activeView
    } = useApp();
    const [scope, setScope] = useState<BoardScope>('my');
    const [search, setSearch] = useState('');
    const [priority, setPriority] = useState<'all' | TicketPriority>('all');
    const [departmentId, setDepartmentId] = useState('all');
    const [assigneeId, setAssigneeId] = useState('all');
    const [draggedTicketId, setDraggedTicketId] = useState<string | null>(null);
    const [dragTarget, setDragTarget] = useState<BoardStatus | null>(null);
    const [updatingTicketId, setUpdatingTicketId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const currentProject = useMemo(
        () => activeView.id !== 'all' ? projects.find(project => project.id === activeView.id) : undefined,
        [activeView.id, projects]
    );

    const filteredTickets = useMemo(() => {
        const query = search.trim().toLowerCase();
        return tickets
            .filter(ticket => !ticket.deleted_at && ticket.status !== 'deleted')
            .filter(ticket => !currentProject || ticket.projects?.some(project => project.project_id === currentProject.id))
            .filter(ticket => Boolean(currentProject) || scope === 'all' || (
                ticket.created_by === currentUser?.id ||
                ticket.assignees?.some(assignee => assignee.id === currentUser?.id) ||
                ticket.collaborators?.some(collaborator => collaborator.id === currentUser?.id)
            ))
            .filter(ticket => priority === 'all' || ticket.priority === priority)
            .filter(ticket => departmentId === 'all' || ticket.department_id === departmentId)
            .filter(ticket => assigneeId === 'all' || ticket.assignees?.some(assignee => assignee.id === assigneeId))
            .filter(ticket => !query || (
                ticket.title.toLowerCase().includes(query) ||
                ticket.description?.toLowerCase().includes(query) ||
                String(ticket.ticket_number).includes(query) ||
                ticket.tags?.some(tag => tag.toLowerCase().includes(query))
            ))
            .sort((left, right) => {
                const priorityDifference = priorityRank[left.priority] - priorityRank[right.priority];
                if (priorityDifference !== 0) return priorityDifference;
                const leftDue = left.due_date ? new Date(left.due_date).getTime() : Number.MAX_SAFE_INTEGER;
                const rightDue = right.due_date ? new Date(right.due_date).getTime() : Number.MAX_SAFE_INTEGER;
                return leftDue - rightDue;
            });
    }, [tickets, currentProject, scope, currentUser?.id, priority, departmentId, assigneeId, search]);

    const ticketsByStatus = useMemo(() => {
        const grouped = new Map<BoardStatus, Ticket[]>(BOARD_COLUMNS.map(column => [column.id, []]));
        for (const ticket of filteredTickets) {
            if (ticket.status !== 'deleted') grouped.get(ticket.status)?.push(ticket);
        }
        return grouped;
    }, [filteredTickets]);

    const moveTicket = async (ticket: Ticket, status: BoardStatus) => {
        if (ticket.status === status || updatingTicketId) return;
        if (currentProject && !currentProject.permissions.can_edit_items) {
            setError('You have view-only access to this project.');
            return;
        }
        setError(null);
        setUpdatingTicketId(ticket.id);
        try {
            await updateTicket(ticket.id, { status });
        } catch (updateError: any) {
            setError(updateError?.message || 'Could not move the ticket.');
        } finally {
            setUpdatingTicketId(null);
            setDraggedTicketId(null);
            setDragTarget(null);
        }
    };

    const handleDrop = async (status: BoardStatus) => {
        const ticket = tickets.find(item => item.id === draggedTicketId);
        if (ticket) await moveTicket(ticket, status);
        else {
            setDraggedTicketId(null);
            setDragTarget(null);
        }
    };

    const clearFilters = () => {
        setSearch('');
        setPriority('all');
        setDepartmentId('all');
        setAssigneeId('all');
    };

    const hasFilters = Boolean(search || priority !== 'all' || departmentId !== 'all' || assigneeId !== 'all');

    return (
        <section className="ticket-board-page" aria-label="Ticket board">
            <header className="ticket-board-header">
                <div>
                    <div className="ticket-board-title">
                        <Columns3 size={22} color={currentProject?.color} aria-hidden="true" />
                        <h1>{currentProject?.name || 'Ticket Board'}</h1>
                    </div>
                    <p>{currentProject?.description || 'Move work through the workflow and see priorities at a glance.'}</p>
                </div>
                <div className="project-workspace-actions">
                    {currentProject && <button className="btn btn-secondary" onClick={() => setActiveView({ type: 'project-list', id: currentProject.id })}><List size={15} /> List</button>}
                    <button className="btn btn-primary" onClick={() => setShowCreateTicket(true)} disabled={Boolean(currentProject && !currentProject.permissions.can_edit_items)}>
                        <Plus size={16} /> New ticket
                    </button>
                </div>
            </header>

            <div className="ticket-board-toolbar">
                {currentProject ? (
                    <select value={currentProject.id} onChange={event => setActiveView({ type: 'ticket-board', id: event.target.value })} aria-label="Choose project">
                        <option value="all">All accessible work</option>
                        {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                    </select>
                ) : (
                    <div className="ticket-board-scope" role="group" aria-label="Ticket scope">
                        <button className={scope === 'my' ? 'active' : ''} onClick={() => setScope('my')}>My work</button>
                        <button className={scope === 'all' ? 'active' : ''} onClick={() => setScope('all')}>All accessible</button>
                    </div>
                )}
                <label className="ticket-board-search">
                    <Search size={15} aria-hidden="true" />
                    <span className="sr-only">Search tickets</span>
                    <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search title, number, or tag" />
                </label>
                <select value={priority} onChange={event => setPriority(event.target.value as 'all' | TicketPriority)} aria-label="Filter by priority">
                    <option value="all">All priorities</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                </select>
                <select value={departmentId} onChange={event => setDepartmentId(event.target.value)} aria-label="Filter by department">
                    <option value="all">All departments</option>
                    {departments.map(department => <option key={department.id} value={department.id}>{department.name}</option>)}
                </select>
                <select value={assigneeId} onChange={event => setAssigneeId(event.target.value)} aria-label="Filter by assignee">
                    <option value="all">All assignees</option>
                    {users.map(user => <option key={user.id} value={user.id}>{user.name}</option>)}
                </select>
                {hasFilters && (
                    <button className="btn btn-ghost btn-sm" onClick={clearFilters} title="Clear filters">
                        <X size={14} /> Clear
                    </button>
                )}
            </div>

            {error && <div className="ticket-board-error" role="alert"><AlertTriangle size={16} /> {error}</div>}

            <div className="ticket-board" role="region" aria-label="Kanban columns">
                {BOARD_COLUMNS.map(column => {
                    const ColumnIcon = column.icon;
                    const columnTickets = ticketsByStatus.get(column.id) || [];
                    const isDropTarget = dragTarget === column.id;
                    return (
                        <section
                            key={column.id}
                            className={`ticket-board-column ${isDropTarget ? 'drop-target' : ''}`}
                            onDragOver={event => {
                                event.preventDefault();
                                setDragTarget(column.id);
                            }}
                            onDragLeave={event => {
                                if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragTarget(null);
                            }}
                            onDrop={event => {
                                event.preventDefault();
                                void handleDrop(column.id);
                            }}
                        >
                            <header className="ticket-board-column-header">
                                <span className="ticket-board-column-name">
                                    <ColumnIcon size={15} color={column.color} aria-hidden="true" />
                                    {column.label}
                                </span>
                                <span className="ticket-board-count">{columnTickets.length}</span>
                            </header>

                            <div className="ticket-board-cards">
                                {columnTickets.map(ticket => {
                                    const overdue = Boolean(
                                        ticket.due_date && new Date(ticket.due_date) < new Date() &&
                                        !['resolved', 'closed'].includes(ticket.status)
                                    );
                                    return (
                                        <article
                                            key={ticket.id}
                                            className={`ticket-board-card ${draggedTicketId === ticket.id ? 'dragging' : ''} ${updatingTicketId === ticket.id ? 'updating' : ''}`}
                                            draggable={updatingTicketId !== ticket.id && (!currentProject || currentProject.permissions.can_edit_items)}
                                            onDragStart={event => {
                                                event.dataTransfer.effectAllowed = 'move';
                                                event.dataTransfer.setData('text/plain', ticket.id);
                                                setDraggedTicketId(ticket.id);
                                            }}
                                            onDragEnd={() => {
                                                setDraggedTicketId(null);
                                                setDragTarget(null);
                                            }}
                                            onClick={() => setActiveView({ type: 'ticket', id: ticket.id })}
                                        >
                                            <div className="ticket-board-card-topline">
                                                <span className="ticket-board-grip" title="Drag to move"><GripVertical size={15} /></span>
                                                <span className="ticket-board-number">#{ticket.ticket_number}</span>
                                                <span className={`priority-badge ${ticket.priority}`}>{ticket.priority}</span>
                                            </div>
                                            <h2>{ticket.title}</h2>
                                            {ticket.tags && ticket.tags.length > 0 && (
                                                <div className="ticket-board-tags">
                                                    {ticket.tags.slice(0, 3).map(tag => <span key={tag}>{tag}</span>)}
                                                    {ticket.tags.length > 3 && <span>+{ticket.tags.length - 3}</span>}
                                                </div>
                                            )}
                                            <div className="ticket-board-card-meta">
                                                {ticket.due_date && (
                                                    <span className={overdue ? 'overdue' : ''} title={overdue ? 'Overdue' : 'Due date'}>
                                                        <CalendarDays size={13} />
                                                        {new Date(ticket.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                    </span>
                                                )}
                                                <div className="ticket-board-avatars" aria-label="Assignees">
                                                    {(ticket.assignees || []).slice(0, 3).map(assignee => (
                                                        <span key={assignee.id} title={assignee.name}>
                                                            {assignee.name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase()}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <label className="ticket-board-status-control" onClick={event => event.stopPropagation()}>
                                                <span className="sr-only">Change status for {ticket.title}</span>
                                                <select
                                                    value={ticket.status}
                                                    disabled={updatingTicketId === ticket.id || Boolean(currentProject && !currentProject.permissions.can_edit_items)}
                                                    onChange={event => void moveTicket(ticket, event.target.value as BoardStatus)}
                                                >
                                                    {BOARD_COLUMNS.map(status => <option key={status.id} value={status.id}>Move to {status.label}</option>)}
                                                </select>
                                            </label>
                                        </article>
                                    );
                                })}
                                {columnTickets.length === 0 && (
                                    <div className="ticket-board-empty">Drop tickets here</div>
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>
        </section>
    );
}
